// src/pages/EmployeeDetail.jsx (MyProfile)
import React, { useEffect, useState } from "react";
import Axios from "../utils/Axios";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Nav from "../components/Nav";
import "../styles/EmployeeDetail.css";
import Toast from "../utils/Toast";

import AuthHandler from "../utils/AuthHandler";
import validateForm, {
  StatusForTimesheet,
  StatusForTask,
} from "../utils/Validation";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap/dist/css/bootstrap.min.css";

import { useDispatch, useSelector } from "react-redux";
import { DOCUMENT_UPLOAD } from "../config/API_Collection";
import {
  EDIT_EMPLOYEE,
  ROLE_LIST_1,
  LIST_ALL_TIMESHEETS,
} from "../config/API_Collection";
import jobTitlesData from "../config/jobTitles.json";

import { AddTimeEmployee } from "../components/AddTimeEmployee";
import TimeSheetApproval from "../components/TimesheetApproval";
import AccessRestricted from "../components/AccessRestricted";
import {
  GET_MY_TASKS_REQUEST,
  GET_WEEKLY_TS_REQUEST,
} from "../store/Actions/Actions";

function FullPageLoaderLayout() {
  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        <div className="top-loading-bar" />
        <div className="employee-detail-container p-3">
          <div className="loading-wrapper">
            <div className="spinner-border text-secondary" role="status" />
          </div>
        </div>
      
      </div>
    </div>
  );
}

function RestrictedLayout({ homePath = "client" }) {
  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        <div className="h-100">
          <AccessRestricted homePath={homePath} />
        </div>
      
      </div>
    </div>
  );
}

export default function MyProfile() {
  const navigate = useNavigate();
  const { id: empid } = useParams();
  const dispatch = useDispatch();

  const loginState = useSelector((s) => s?.login);
  const actions = loginState?.actions || null;
  const isBootstrapping = !!loginState?.isBootstrapping;

  const canViewEmployee = actions?.employees?.employee_details === true;

  const myTaskList = useSelector((s) => s?.myTasksList?.myTasks) || [];
  const weekList = useSelector((s) => s?.weeklyTimesheets?.timesheets) || [];

  const [isLoading, setIsLoading] = useState(true);
  const [employeeData, setEmployeeData] = useState([]);
  const [toast, setToast] = useState({ message: "", type: "", triggerId: 0 });
  const [formErrors, setFormErrors] = useState({});
  const [activeTab, setActiveTab] = useState("timesheets");

  const [showModalEdit, setShowModalEdit] = useState(false);

  const [showAddTime, setShowAddTime] = useState(false);
  const [timeSheetApro, setTimeSheetApro] = useState(false);
  const [weekProp, setWeekProp] = useState(null);

  const [roleLists, setRoleLists] = useState([]);
  const [isUploadingProfile, setIsUploadingProfile] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState("/assets/images/default-profile.jpg");
  const [profileUploadData, setProfileUploadData] = useState(null); // { document_id, blob_path, file_name }

  // All Timesheets (accordion) data
  const [allTSLoading, setAllTSLoading] = useState(false);
  const [allTSError, setAllTSError] = useState("");
  const [allEmployees, setAllEmployees] = useState([]);
  const [openEmpId, setOpenEmpId] = useState(null);
  const [expandedEmp, setExpandedEmp] = useState({});

  const can_see_all_timsheet =
    actions?.timesheets?.list_all_timesheets === true;
  const can_see_tasklist = actions?.tasks?.task_list;

  const showToast = (message, type) =>
    setToast({ message, type, triggerId: Date.now() });

  useEffect(() => {
    fetchEmployeeDetails();
    fetchJobRole();
  }, []);

  useEffect(() => {
    dispatch({ type: GET_MY_TASKS_REQUEST, payload: null });
    dispatch({ type: GET_WEEKLY_TS_REQUEST, payload: null });
  }, [dispatch]);

  // Preload All Timesheets list so tab is instant
  useEffect(() => {
    if (canViewEmployee) fetchAllTimesheet();
  }, [canViewEmployee]);

  const fetchJobRole = async () => {
    try {
      const response = await Axios.get(ROLE_LIST_1);
      setRoleLists(response?.data);
    } catch (e) { }
  };

  const fetchEmployeeDetails = async () => {
    try {
      setIsLoading(true);
      const response = await Axios.get(`/employees/profile`);
      if (response.status === 200) {
        const data = response.data.data || {};
        setEmployeeData(data || []);

        // Derive profile image URL from common response shapes so it persists after reload
        const pickFirst = (arr) => arr.find((v) => v && typeof v === "string");
        const candidates = [
          data?.profile_data?.blob_path,
          data?.profile_data?.url,
          data?.personal?.blob_path,
          data?.personal?.blob_path_url,
          data?.personal?.photo,
          data?.personal?.profile_image,
          data?.blob_path,
          data?.file_url,
          data?.url,
          data?.profile_blob,
          data?.profile_url,
        ];
        const found = pickFirst(candidates) || null;
        if (found) setProfileImageUrl(found);
      }
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
    } finally {
      setIsLoading(false);
    }
  };

  // Profile picture upload handler
  const onPickProfileImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    // Use edited values if available in form, fallback to current profile names
    const form = document.getElementById("editProfileForm");
    const first = (form?.elements?.first_name?.value ?? employeeData?.personal?.first_name) || "";
    const last = (form?.elements?.last_name?.value ?? employeeData?.personal?.last_name) || "";
    const profile_name = `${first} ${last}`.trim() || file.name;
    setIsUploadingProfile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_name", profile_name);
      formData.append("section", "profile");
      const res = await Axios.post(DOCUMENT_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = res?.data?.data || {};
      // Prefer server-returned URL/blob path, fallback to local preview
      const returnedUrl = data?.blob_path || data?.url || data?.file_url || data?.profile_url || null;
      if (returnedUrl) {
        setProfileImageUrl(returnedUrl);
      } else {
        const localUrl = URL.createObjectURL(file);
        setProfileImageUrl(localUrl);
      }
      setProfileUploadData({
        document_id: data?.document_id || data?.id,
        blob_path: returnedUrl,
        file_name: data?.file_name || profile_name,
      });
      // expose briefly to other pages in case they want to persist it (best-effort)
      try {
        window.myProfileUploadData = {
          document_id: data?.document_id || data?.id,
          blob_path: returnedUrl || data?.blob_path,
          file_name: data?.file_name || profile_name,
        };
      } catch (e) {
        // ignore
      }
      showToast("Profile image uploaded", "success");
    } catch (err) {
      showToast("Failed to upload profile image", "danger");
    } finally {
      setIsUploadingProfile(false);
    }
  };

  // === REFRESHABLE: This is what we want to call after TS approve/reject on Close ===
  const fetchAllTimesheet = async () => {
    if (actions?.timesheets?.list_all_timesheets) {
      setAllTSError("");
      try {
        setAllTSLoading(true);
        const res = await Axios.get(LIST_ALL_TIMESHEETS);
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        const mapped = list.map((e) => {
          const weeks = (e.timesheets || []).map((w) => ({
            week_no: w.week_no,
            week_date: String(w.week || ""),
            week_start: w.week_start,
            total_hours: w.total_hours ?? 0,
            progress: w.progress,
            status: w.status ?? null,
            timesheet_id: w.timesheet_id,
            is_current_week: false,
          }));
          return {
            employee_id: e.emp_id,
            name: e.emp_name,
            last_submitted_at: e.last_submitted_at,
            new_submissions: e.newly_submitted_count ?? 0,
            weeks,
          };
        });
        setAllEmployees(mapped);
      } catch (error) {
        setAllTSError("Failed to load all timesheets.");
        AuthHandler(error?.response?.status, setToast, navigate);
      } finally {
        setAllTSLoading(false);
      }
    }
  };

  const openModalEdit = () => setShowModalEdit(true);
  const closeModalEdit = () => setShowModalEdit(false);

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    const formData = e.target;
    try {
      const payload = {
        employee_id: parseInt(employeeData.personal?.employee_id, 10),
        first_name: formData.first_name.value,
        last_name: formData.last_name.value,
        phone: formData.phone.value,
        job_title: formData.job_title.value,
        role_id: parseInt(formData.role.value, 10),
        billable: formData.billable_status.value,
        billable_rate: formData.billable_rate.value,
        office: formData.office?.value,
      };

      // If a profile was uploaded, include its document id / URL so backend can persist it
      if (profileUploadData?.document_id) {
        payload.profile_document_id = profileUploadData.document_id;
      }
      if (profileUploadData?.blob_path) {
        payload.profile_image_url = profileUploadData.blob_path;
      }

      const requiredFields = [
        "first_name",
        "last_name",
        "phone",
        "job_title",
        "role_id",
        "billable",
        "billable_rate",
        "office",
      ];
      const errors = validateForm(payload, requiredFields);
      setFormErrors(errors);
      if (Object.keys(errors).length > 0) return;

      const response = await Axios.post(EDIT_EMPLOYEE, payload);
      if (response.status === 200) {
        showToast("Profile updated Successfully!", "success");
        closeModalEdit();
        fetchEmployeeDetails();
      }
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
    }
  };

  const progressColor = (v) => {
    if (v?.progress > 100) return "bg-danger";
    if (v?.progress <= 100 && v?.progress >= 80) return "bg-success";
    if (v?.progress < 80) return "bg-warning";
    return "bg-info";
  };

  if (isBootstrapping) return <FullPageLoaderLayout />;
  if (!canViewEmployee) return <RestrictedLayout homePath="client" />;

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        {isLoading && <div className="top-loading-bar" />}
        <Toast
          message={toast.message}
          type={toast.type}
          triggerId={toast.triggerId}
        />

        <style>{`
          .tight-row > td, .tight-row > th { padding-top: 3px !important; padding-bottom: 3px !important; }
          .pointer { cursor: pointer; }
        `}</style>

        <div className="employee-detail-container p-3">
          {/* Header */}
          <div className="row align-items-center">
            <div className="col-md-2 text-center">
              <img
                src={profileImageUrl}
                alt="Profile"
                className="profile-pic-detail-view rounded-circle"
                style={{ width: 120, height: 120, objectFit: "cover" }}
              />
            </div>
            <div className="col-md-6">
              <h2 className="employee-name">
                {employeeData.personal?.first_name}{" "}
                {employeeData.personal?.last_name}
              </h2>
              <div className="mb-1 text-muted">
                {employeeData.personal?.role_name}
                {employeeData.personal?.job_title && ` - ${employeeData.personal?.job_title}`}
              </div>
              <div className="text-smaller text-secondary">
                <div>Email: {employeeData.personal?.email}</div>
                <div>Phone: {employeeData.personal?.phone}</div>
              </div>
            </div>
            <div className="col-md-3 justify-content-end">
              <div className="text-smaller text-secondary">
                <div>
                  <i className="bi bi-calendar3"></i> Joining Date:{" "}
                  {employeeData.personal?.created_at}
                </div>
                <div className="mt-2">
                  <i className="bi bi-geo-alt-fill"></i> Location:{" "}
                  {employeeData.personal?.office}
                </div>
              </div>
            </div>
            <div className="col-md-1 d-flex justify-content-end">
              <div className="dropdown">
                <button
                  className="btn btn-sm"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="bi bi-three-dots-vertical"></i>
                </button>
                <ul className="dropdown-menu">
                  <li onClick={openModalEdit}>
                    <a className="dropdown-item" href="#">
                      Edit My Profile
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row align-items-center">
            <hr className="prf-sep-hr" />
          </div>

          {/* Tabs */}
          <div className="tab-view-container p-3 pt-2">
            <ul className="nav nav-tabs">
              <li className="nav-item">
                {actions?.timesheets?.list_all_timesheets && (
                  <button
                    className={`nav-link ${activeTab === "all_timesheet" ? "active" : ""
                      }`}
                    onClick={() => setActiveTab("all_timesheet")}
                  >
                    All Timesheets
                  </button>
                )}
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "timesheets" ? "active" : ""
                    }`}
                  onClick={() => setActiveTab("timesheets")}
                >
                  Time Sheets
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "tasks" ? "active" : ""
                    }`}
                  onClick={() => setActiveTab("tasks")}
                >
                  Task List
                </button>
              </li>
            </ul>

            {/* Content */}
            <div className="tab-content border p-3">
              {showAddTime && (
                <AddTimeEmployee
                  setShowAddTime={setShowAddTime}
                  weekProp={weekProp}
                />
              )}

              {/* My weeks */}
              {activeTab === "timesheets" && (
                <div>
                  <table className="table custom-table">
                    <thead className="table-light">
                      <tr>
                        <th>Week</th>
                        <th>Total Hours</th>
                        <th>Progress</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {weekList.length > 0 ? (
                        weekList.map((v, idx) => (
                          <tr
                            key={idx}
                            onClick={() => {
                              setShowAddTime(true);
                              setWeekProp(v);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            {v?.is_current_week ? (
                              <td className="text-primary">{`${v?.week_date} - This Week `}</td>
                            ) : (
                              <td>{v?.week_date}</td>
                            )}
                            <td>{v?.total_hours} hrs</td>
                            <td>
                              <div
                                className="progress"
                                role="progressbar"
                                aria-valuemin="0"
                                aria-valuemax="100"
                                style={{ height: 10 }}
                              >
                                <div
                                  className={`progress-bar ${progressColor(v)}`}
                                  style={{ width: `${v?.progress || 0}%` }}
                                />
                              </div>
                              <div className="small text-muted">
                                {v?.progress}%
                              </div>
                            </td>
                            <td>
                              <StatusForTimesheet code={v?.status} />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="text-center">
                            No Data Available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* My tasks */}
              {activeTab === "tasks" && (
                <div>
                  <table className="custom-table table">
                    <thead className="table-light">
                      <tr>
                        <th>Task ID</th>
                        <th>Project</th>
                        <th>Task</th>
                        <th>Assigned On</th>
                        <th>Allocated Hours</th>
                        <th>Allocated Rate</th>
                        <th>Worked Hours</th>
                        <th>Completion</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myTaskList.length > 0 ? (
                        myTaskList.map((v) => (
                          <tr key={v?.task_id}>
                            <td>T{v?.project_no}</td>
                            <td>{v?.project_name}</td>
                            <td>{v?.task_name}</td>
                            <td>{v?.assignment_date}</td>
                            <td>{v?.allocated_hours} hrs</td>
                            <td>$ {v?.allocated_rate}</td>
                            <td>{v?.worked_hours} hrs</td>
                            <td>
                              <div
                                className="progress"
                                role="progressbar"
                                aria-valuemin="0"
                                aria-valuemax="100"
                                style={{ height: 10 }}
                              >
                                <div
                                  className={`progress-bar ${progressColor(v)}`}
                                  style={{ width: `${v?.progress || 0}%` }}
                                />
                              </div>
                              <div className="small text-muted">
                                {v?.progress}%
                              </div>
                            </td>
                            <td>
                              <StatusForTask code={v?.status} />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={9} className="text-center">
                            No Data Available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* All Timesheets */}
              {activeTab === "all_timesheet" && (
                <div>
                  {allTSLoading && (
                    <div className="d-flex align-items-center gap-2 text-muted mb-2">
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      />
                      <span>Loading all timesheets…</span>
                    </div>
                  )}
                  {allTSError && (
                    <div className="alert alert-danger py-2">{allTSError}</div>
                  )}

                  {!allTSLoading && !allTSError && (
                    <div className="table-responsive">
                      <table className="table custom-table mb-0">
                        <tbody>
                          {Array.isArray(allEmployees) &&
                            allEmployees.length > 0 ? (
                            allEmployees.map((emp) => {
                              const isOpen = openEmpId === emp.employee_id;
                              const isExpanded = !!expandedEmp[emp.employee_id];
                              const weeks = Array.isArray(emp.weeks)
                                ? emp.weeks
                                : [];
                              const visibleWeeks = isExpanded
                                ? weeks
                                : weeks.slice(0, 5);
                              const remaining =
                                weeks.length > 5 ? weeks.length - 5 : 0;

                              return (
                                <React.Fragment key={emp.employee_id}>
                                  <tr
                                    className="pointer"
                                    onClick={() =>
                                      setOpenEmpId(
                                        isOpen ? null : emp.employee_id
                                      )
                                    }
                                  >
                                    <td className="align-middle" style={{ width: 32 }}>
                                      <i
                                        className={`bi ${isOpen
                                            ? "bi-caret-down-fill"
                                            : "bi-caret-right-fill"
                                          }`}
                                      />
                                    </td>
                                    <td
                                      className={`align-middle ${emp.new_submissions > 0
                                          ? "fw-semibold"
                                          : ""
                                        }`}
                                    >
                                      {emp.name}
                                    </td>
                                    <td
                                      className={`align-middle ${emp.new_submissions > 0
                                          ? "fw-semibold"
                                          : ""
                                        }`}
                                    >
                                      {Number(emp?.new_submissions || 0)} New
                                      Timesheet Submissions
                                    </td>
                                    <td className="align-middle text-end">
                                      Last submitted on {emp.last_submitted_at}
                                    </td>
                                  </tr>

                                  {isOpen && (
                                    <tr>
                                      <td colSpan={4} className="p-0">
                                        <div className="pt-3 border-top bg-white">
                                          <div className="table-responsive">
                                            <table className="table table-sm mb-0">
                                              <thead>
                                                <tr className="text-muted tight-row">
                                                  <th className="text-start" style={{ width: "20%" }}>
                                                    Week
                                                  </th>
                                                  <th className="text-center">
                                                    Total Hours
                                                  </th>
                                                  <th className="text-center" style={{ width: "30%" }}>
                                                    Week Progress
                                                  </th>
                                                  <th className="text-end" style={{ width: "20%" }}>
                                                    Status
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {visibleWeeks.length > 0 ? (
                                                  visibleWeeks.map((w, idx) => (
                                                    <tr
                                                      key={`${emp.employee_id}-week-${w?.timesheet_id || w?.week_no || idx}`}
                                                      className="pointer"
                                                      onClick={() => {
                                                        // open approval modal (read-only) for this week
                                                        setWeekProp(w);
                                                        setTimeSheetApro(true);
                                                      }}
                                                    >
                                                      <td className={`align-middle text-start ${w?.is_current_week ? "text-primary" : ""}`}>
                                                        {w?.week_date || `Week ${w?.week_no ?? ""}`}
                                                      </td>
                                                      <td className="align-middle text-center">
                                                        {w?.total_hours ?? 0} hrs
                                                      </td>
                                                      <td className="align-middle text-center">
                                                        <div
                                                          className="progress mx-auto"
                                                          role="progressbar"
                                                          aria-valuemin="0"
                                                          aria-valuemax="100"
                                                          style={{ height: 10 }}
                                                          title={`${w?.progress || 0}%`}
                                                        >
                                                          <div
                                                            className={`progress-bar ${w?.progress > 100
                                                                ? "bg-danger"
                                                                : w?.progress >= 80
                                                                  ? "bg-success"
                                                                  : w?.progress > 0
                                                                    ? "bg-warning"
                                                                    : "bg-info"
                                                              }`}
                                                            style={{ width: `${w?.progress || 0}%` }}
                                                          />
                                                        </div>
                                                        <div className="small text-muted">
                                                          {w?.progress}%
                                                        </div>
                                                      </td>
                                                      <td className="align-middle text-end">
                                                        <StatusForTimesheet code={w?.status} />
                                                      </td>
                                                    </tr>
                                                  ))
                                                ) : (
                                                  <tr className="tight-row">
                                                    <td colSpan={4} className="text-center text-muted py-3">
                                                      No weeks to display
                                                    </td>
                                                  </tr>
                                                )}
                                              </tbody>
                                            </table>
                                          </div>

                                          {remaining > 0 && (
                                            <div className="d-flex justify-content-start py-2 px-2">
                                              <a
                                                href="#"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  setExpandedEmp((prev) => ({
                                                    ...prev,
                                                    [emp.employee_id]: !isExpanded,
                                                  }));
                                                }}
                                              >
                                                {isExpanded
                                                  ? "Show less"
                                                  : `Show ${remaining} more weeks`}
                                              </a>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={4} className="text-center py-4">
                                No Data Available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        
        </div>
      </div>

      {/* Edit Profile Modal */}
      {showModalEdit && (
        <div className="custom-project-modal">
          <div className="modal-backdrop fade show" />
          <div
            className="modal fade show"
            tabIndex="-1"
            aria-labelledby="exampleModalLabel"
            aria-hidden="true"
            style={{ display: "block", zIndex: 1050 }}
          >
            <div className="modal-dialog modal-lg mx-auto" style={{ marginTop: "2%" }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="exampleModalLabel">Update My Profile</h5>
                </div>
                <div className="modal-body">
                  <div className="add-budget-container">
                    <form id="editProfileForm" className="budget-form" onSubmit={handleEditEmployee}>
                      <div className="form-row">
                        <div className={`form-group flex-item ${formErrors.first_name ? "has-error" : ""}`}>
                          <label>First Name *</label>
                          <input type="text" name="first_name" defaultValue={employeeData.personal?.first_name} />
                        </div>
                        <div className={`form-group flex-item ${formErrors.last_name ? "has-error" : ""}`}>
                          <label>Last Name *</label>
                          <input type="text" name="last_name" defaultValue={employeeData.personal?.last_name} />
                        </div>
                      </div>

                      {/* Profile photo upload */}
                      {/*
                      <div className="form-row">
                        <div className="form-group flex-item">
                          <label>Profile Photo</label>
                          <div className="d-flex align-items-center gap-3">
                            <div style={{ position: "relative", width: 64, height: 64 }}>
                              <img
                                src={profileImageUrl}
                                alt="Preview"
                                className="rounded-circle"
                                style={{ width: 64, height: 64, objectFit: "cover" }}
                              />

                              {/* small overlay button on modal preview */}
                              {/* <button
                                type="button"
                                onClick={() => document.getElementById("profilePicInputModal").click()}
                                disabled={isUploadingProfile}
                                title={isUploadingProfile ? "Uploading..." : "Upload profile image"}
                                className="btn btn-light rounded-circle"
                                style={{
                                  position: "absolute",
                                  right: -6,
                                  bottom: -6,
                                  width: 30,
                                  height: 30,
                                  padding: 4,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
                                  fontSize: 12,
                                }}
                              >
                                {isUploadingProfile ? (
                                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                ) : (
                                  <i className="bi bi-plus" style={{ fontSize: 16 }} />
                                )}
                              </button>
                            </div>
                            <div>
                              <input
                                id="profilePicInputModal"
                                type="file"
                                accept="image/*"
                                className="d-none"
                                onChange={onPickProfileImage}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="form-group flex-item" />
                      </div> */}

                      <div className="form-row">
                        <div className="form-group flex-item">
                          <label>Email *</label>
                          <input type="email" name="email" defaultValue={employeeData.personal?.email} readOnly />
                        </div>
                        <div className={`form-group flex-item ${formErrors.phone ? "has-error" : ""}`}>
                          <label>Phone *</label>
                          <input type="text" name="phone" defaultValue={employeeData.personal?.phone} minLength={9} maxLength={12} />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className={`form-group flex-item ${formErrors.role ? "has-error" : ""}`}>
                          <label>Job Role *</label>
                          <select name="role" defaultValue={employeeData.personal?.role_id} disabled>
                            {roleLists?.data?.length > 0 &&
                              roleLists.data.map((v) => (
                                <option key={v.role_id} value={v.role_id}>{v.role_name}</option>
                              ))}
                          </select>
                        </div>
                        <div className={`form-group flex-item ${formErrors.job_title ? "has-error" : ""}`}>
                          <label>Job Title *</label>
                          <select name="job_title" defaultValue={employeeData.personal?.job_title} disabled>
                            <option value="">-- Select a Job Title --</option>
                            {jobTitlesData.jobTitles.map((title) => (
                              <option key={title} value={title}>
                                {title}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className={`form-group flex-item ${formErrors.billable_status ? "has-error" : ""}`}>
                          <label>Billable Status *</label>
                          <select name="billable_status" defaultValue={employeeData.personal?.billable} disabled>
                            <option value="1">Billable</option>
                            <option value="0">Non-Billable</option>
                          </select>
                        </div>
                        <div className={`form-group flex-item ${formErrors.billable_rate ? "has-error" : ""}`}>
                          <label>Billable Rate *</label>
                          <input type="text" name="billable_rate" defaultValue={employeeData.personal?.billable_rate} disabled/>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group flex-item">
                          <label>Location *</label>
                          <select name="office" defaultValue={employeeData.personal?.office} disabled>
                            <option value="Hamilton (Head Office)">Hamilton (Head Office)</option>
                            <option value="Rotorua Office">Rotorua Office</option>
                            <option value="Thames Office">Thames Office</option>
                            <option value="Wellington Office">Wellington Office</option>
                            <option value="Auckland Office">Auckland Office</option>
                            <option value="Napier Office">Napier Office</option>
                            <option value="Whitianga Office">Whitianga Office</option>
                            <option value="Asia Office- Sri Lanka">Asia Office- Sri Lanka</option>
                            <option value="Whakatāne Office">Whakatāne Office</option>
                            <option value="Cambridge">Cambridge</option>
                            <option value="Raglan">Raglan</option>
                            <option value="Palmerston North">Palmerston North</option>
                            <option value="Christchurch">Christchurch</option>
                            <option value="New Plymouth">New Plymouth</option>
                          </select>
                        </div>
                        <div className="form-group flex-item" />
                      </div>

                      <div className="form-actions">
                        <button type="submit" className="btn btn-primary">Update Profile</button>
                        <button type="button" className="btn btn-secondary" onClick={closeModalEdit}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timesheet Approval Modal — pass refresh callback */}
      {timeSheetApro && (
        <TimeSheetApproval
          setTimeSheetApro={setTimeSheetApro}
          weekProp={weekProp}
          emp_id={openEmpId}
          onRefreshAllTimesheets={fetchAllTimesheet}
        />
      )}
    </div>
  );
}
